/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IMoeV2TableViewProps, ICellPosition, IRichTextContent } from './moe-v2-table';

export default (props: IMoeV2TableViewProps) => {
    const {
        config,
        resources,
        isEditMode,
        tableState,
        editingContent,
        processedHeaders,
        processedRows,
        handleCellClick,
        handleCellDoubleClick,
        handleEditComplete,
        handleKeyDown,
        updateEditingContent
    } = props;

    const tableRef = React.useRef<HTMLTableElement>(null);
    const editingInputRef = React.useRef<HTMLTextAreaElement>(null);

    // Focus editing input when editing starts
    React.useEffect(() => {
        if (tableState.editingCell && editingInputRef.current) {
            editingInputRef.current.focus();
            editingInputRef.current.select();
        }
    }, [tableState.editingCell]);

    // Helper functions for rendering
    const isCellSelected = (rowIndex: number, cellIndex: number): boolean => {
        return tableState.selectedCells.some((pos: ICellPosition) => pos.rowIndex === rowIndex && pos.cellIndex === cellIndex);
    };

    const isCellEditing = (rowIndex: number, cellIndex: number): boolean => {
        return tableState.editingCell?.rowIndex === rowIndex && tableState.editingCell?.cellIndex === cellIndex;
    };

    // Render cell content with editing support
    const renderCellContent = (richText: IRichTextContent | undefined, rowIndex: number, cellIndex: number): React.ReactNode => {
        const isEditing = isCellEditing(rowIndex, cellIndex);

        if (isEditing && isEditMode) {
            return (
                <textarea
                    ref={editingInputRef}
                    value={editingContent}
                    onChange={e => updateEditingContent(e.target.value)}
                    onBlur={() => handleEditComplete(true)}
                    onKeyDown={e => {
                        e.stopPropagation();
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleEditComplete(true);
                        } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleEditComplete(false);
                        }
                    }}
                    className='moe-table-cell-editor'
                    aria-label={resources.editCellLabel}
                    rows={Math.max(1, editingContent.split('\n').length)}
                />
            );
        }

        return <div className='moe-table-cell-content'>{renderRichTextContent(richText)}</div>;
    };

    // Generate table styles based on configuration
    const getTableStyles = (): React.CSSProperties => ({
        minWidth: config.responsive?.minWidth || '100%',
        borderCollapse: 'collapse',
        width: '100%',
        textAlign: config.styling?.textAlign || 'left',
        border: config.styling?.borderStyle !== 'none' ? `1px ${config.styling?.borderStyle || 'solid'} #dee2e6` : 'none'
    });

    // Generate cell styles
    const getCellStyles = (
        rowIndex: number,
        cellIndex: number,
        backgroundColor: string,
        width: string,
        verticalAlign?: string,
        textAlign?: string
    ): React.CSSProperties => {
        const isSelected = isCellSelected(rowIndex, cellIndex);
        // Use cell-specific textAlign if provided, otherwise fall back to global styling.textAlign

        const effectiveTextAlign = textAlign || config.styling?.textAlign || 'left';

        return {
            border: config.styling?.borderStyle !== 'none' ? `1px ${config.styling?.borderStyle || 'solid'} #dee2e6` : 'none',
            position: 'relative',
            textAlign: effectiveTextAlign as React.CSSProperties['textAlign'],
            backgroundColor: backgroundColor,
            width: `${width}`,
            verticalAlign: verticalAlign || 'top',
            ...(isSelected && isEditMode && {})
        };
    };

    // Helper function to render rich text content as HTML
    const renderRichTextContent = (richText?: IRichTextContent): React.ReactNode => {
        if (!richText) {
            return null;
        }

        return <div dangerouslySetInnerHTML={{ __html: ((richText as unknown) as string) || '' }} />;
    };

    return (
        <div
            className='ms-text-block moe-v2-module-container moe-v2-table-module'
            style={
                {
                    paddingTop: config.topPadding || '0px',
                    '--cell-padding': config.styling?.cellPadding || '0.75rem',
                    backgroundColor: config.headingBackgroundColor || '#f8f9fa',
                    textAlign: config.headingAlign || 'left',
                    verticalAlign: config.headingVerticalAlign || 'top'
                } as React.CSSProperties
            }
        >
            {/* Configurable Table Heading */}
            {config.tableHeading && (
                <div className='moe-table-heading-container' style={{ backgroundColor: config.headingBackgroundColor || '#f8f9fa' }}>
                    <div className='moe-table-heading' dangerouslySetInnerHTML={{ __html: config.tableHeading as string }} />
                </div>
            )}

            {/* Table with Horizontal Scroll Support */}
            <div
                className={`moe-table-container ${config.responsive?.enableHorizontalScroll ? 'scrollable' : ''}`}
                style={{ paddingTop: config.tableTopPadding || '0px' }}
            >
                <table
                    ref={tableRef}
                    className='moe-table'
                    style={getTableStyles()}
                    onKeyDown={handleKeyDown}
                    tabIndex={isEditMode ? 0 : undefined}
                    role='table'
                >
                    {/* Table Headers */}
                    <thead>
                        <tr className='moe-table-row header-row'>
                            {processedHeaders.map((header, cellIndex) => {
                                const isSelected = isCellSelected(-1, cellIndex); // Use -1 for header row
                                const isEditing = isCellEditing(-1, cellIndex);

                                return (
                                    <th
                                        key={cellIndex}
                                        className={`moe-table-cell header-cell ${isSelected ? 'selected' : ''} ${
                                            isEditing ? 'editing' : ''
                                        }`}
                                        style={getCellStyles(
                                            -1,
                                            cellIndex,
                                            header.backgroundColor || '#e9ecef',
                                            header.width || '0',
                                            header.verticalAlign || 'top',
                                            header.textAlign
                                        )}
                                        colSpan={header.colspan && header.colspan > 1 ? header.colspan : undefined}
                                        rowSpan={header.rowspan && header.rowspan > 1 ? header.rowspan : undefined}
                                        scope='col'
                                        onClick={e => handleCellClick({ rowIndex: -1, cellIndex }, e)}
                                        onDoubleClick={() => handleCellDoubleClick({ rowIndex: -1, cellIndex })}
                                        tabIndex={isEditMode ? 0 : undefined}
                                    >
                                        {renderCellContent(header.content, -1, cellIndex)}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                        {processedRows.map((row, rowIndex) => (
                            <tr key={rowIndex} className='moe-table-row data-row'>
                                {row.cells?.map((cell, cellIndex) => {
                                    const isSelected = isCellSelected(rowIndex, cellIndex);
                                    const isEditing = isCellEditing(rowIndex, cellIndex);

                                    return (
                                        <td
                                            key={cellIndex}
                                            className={`moe-table-cell data-cell ${isSelected ? 'selected' : ''} ${
                                                isEditing ? 'editing' : ''
                                            }`}
                                            style={getCellStyles(
                                                rowIndex,
                                                cellIndex,
                                                cell.backgroundColor || '#ffffff',
                                                cell.width || '0',
                                                cell.verticalAlign || 'top',
                                                cell.textAlign
                                            )}
                                            colSpan={cell.colspan && cell.colspan > 1 ? cell.colspan : undefined}
                                            rowSpan={cell.rowspan && cell.rowspan > 1 ? cell.rowspan : undefined}
                                            onClick={e => handleCellClick({ rowIndex, cellIndex }, e)}
                                            onDoubleClick={() => handleCellDoubleClick({ rowIndex, cellIndex })}
                                            tabIndex={isEditMode ? 0 : undefined}
                                        >
                                            {renderCellContent(cell.content, rowIndex, cellIndex)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Screen Reader Support */}
            <div className='sr-only' aria-live='polite'>
                {tableState.editingCell && 'Editing cell content'}
                {tableState.selectedCells.length > 1 && `${tableState.selectedCells.length} cells selected`}
            </div>
        </div>
    );
};
